import { useState, useEffect, useRef, useCallback } from "react";
import { Activity, Zap, BarChart3, RefreshCw, Brain, ArrowUpRight, ArrowDownRight, AlertTriangle, Bell, BellRing, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { capitalFlowAPI } from "../services/api";

const FLOW_COLORS = {
  ACCUMULATION: { bg: "bg-emerald-500/15", text: "text-emerald-400", border: "border-emerald-500/30" },
  DISTRIBUTION: { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30" },
  EXTREME_VOLUME: { bg: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/30" },
  ELEVATED: { bg: "bg-slate-500/15", text: "text-slate-400", border: "border-slate-500/30" },
};

const SIGNAL_COLORS = {
  BULLISH: "text-emerald-400",
  BEARISH: "text-red-400",
  NEUTRAL: "text-slate-400",
};

// Alert criteria - what makes a flow "hot"
function classifyAlert(item) {
  const reasons = [];
  let level = 0; // 0=normal, 1=interesting, 2=hot, 3=critical

  // Extreme volume
  if (item.vol_ratio >= 7) { reasons.push("Volume extreme >7x"); level = Math.max(level, 3); }
  else if (item.vol_ratio >= 5) { reasons.push("Volume tres eleve >5x"); level = Math.max(level, 2); }

  // Big accumulation (buying + high volume)
  if (item.flow_signal === "ACCUMULATION" && item.vol_ratio >= 3) {
    reasons.push("Accumulation massive");
    level = Math.max(level, 2);
  }

  // Heavy distribution (selling + high volume)
  if (item.flow_signal === "DISTRIBUTION" && item.vol_ratio >= 4) {
    reasons.push("Distribution lourde");
    level = Math.max(level, 2);
  }

  // RSI extremes + volume
  if (item.rsi < 25 && item.vol_ratio >= 3) {
    reasons.push("Survente extreme + volume");
    level = Math.max(level, 3);
  }
  if (item.rsi > 80 && item.vol_ratio >= 3) {
    reasons.push("Surachat extreme + volume");
    level = Math.max(level, 2);
  }

  // Huge daily move + volume
  if (Math.abs(item.daily_change) > 10 && item.vol_ratio >= 3) {
    reasons.push(`Mouvement ${item.daily_change > 0 ? "+" : ""}${item.daily_change}% avec volume`);
    level = Math.max(level, 3);
  }
  if (Math.abs(item.daily_change) > 5 && item.vol_ratio >= 2.5) {
    reasons.push(`Variation forte ${item.daily_change > 0 ? "+" : ""}${item.daily_change}%`);
    level = Math.max(level, item.vol_ratio >= 4 ? 2 : 1);
  }

  return { level, reasons };
}

const ALERT_LEVELS = {
  3: { label: "CRITIQUE", bg: "bg-red-500/20", border: "border-red-500/40", text: "text-red-300", icon: "text-red-400", pulse: true },
  2: { label: "IMPORTANT", bg: "bg-amber-500/15", border: "border-amber-500/30", text: "text-amber-300", icon: "text-amber-400", pulse: false },
  1: { label: "A SURVEILLER", bg: "bg-cyan-500/10", border: "border-cyan-500/20", text: "text-cyan-300", icon: "text-cyan-400", pulse: false },
  0: { label: "", bg: "", border: "border-transparent", text: "text-slate-300", icon: "", pulse: false },
};

function FlowBadge({ signal }) {
  const c = FLOW_COLORS[signal] || FLOW_COLORS.ELEVATED;
  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${c.bg} ${c.text} border ${c.border}`} data-testid={`flow-badge-${signal}`}>
      {signal === "ACCUMULATION" ? "ACHAT" : signal === "DISTRIBUTION" ? "VENTE" : signal === "EXTREME_VOLUME" ? "SWEEP" : "ELEVE"}
    </span>
  );
}

function VolumeBar({ ratio, maxRatio }) {
  const pct = Math.min((ratio / maxRatio) * 100, 100);
  const color = ratio > 5 ? "bg-red-500" : ratio > 3 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="flex items-center gap-1.5 w-24">
      <div className="flex-1 h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] text-slate-300 font-mono w-8 text-right">{ratio}x</span>
    </div>
  );
}

function AlertBanner({ alerts, onTickerClick }) {
  if (alerts.length === 0) return null;
  return (
    <div className="border-b border-red-500/20 bg-gradient-to-r from-red-900/20 via-slate-900/50 to-amber-900/10" data-testid="cf-alert-banner">
      <div className="px-3 py-1.5 flex items-center gap-2">
        <BellRing className="w-3.5 h-3.5 text-red-400 animate-pulse shrink-0" />
        <span className="text-[10px] font-bold text-red-400 uppercase shrink-0">Alertes Smart Money</span>
        <div className="flex-1 overflow-x-auto flex gap-2 scrollbar-thin">
          {alerts.map((a, i) => {
            const style = ALERT_LEVELS[a.alert.level];
            return (
              <button key={i} onClick={() => onTickerClick?.(a.ticker)}
                className={`shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] border ${style.bg} ${style.border} ${style.text} hover:brightness-125 transition-all ${style.pulse ? "animate-pulse" : ""}`}
                data-testid={`cf-alert-${a.ticker}`}>
                <AlertTriangle className={`w-3 h-3 ${style.icon}`} />
                <span className="font-bold">{a.ticker}</span>
                <span className="text-[8px] opacity-70">{a.vol_ratio}x</span>
                <span className={`text-[8px] ${a.daily_change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {a.daily_change >= 0 ? "+" : ""}{a.daily_change}%
                </span>
                <span className="text-[8px] opacity-60 max-w-[120px] truncate">{a.alert.reasons[0]}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function CapitalFlowPanel({ finnhubKey, onTickerClick, onAlertCount, cachedData, setCachedData }) {
  const [data, setData] = useState(cachedData || null);
  const [loading, setLoading] = useState(!cachedData);
  const [aiReport, setAiReport] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [tab, setTab] = useState("flow");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [filter, setFilter] = useState("all"); // all | alerts | buy | sell
  const prevAlertsRef = useRef(new Set());
  const audioRef = useRef(null);

  const playAlertSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgipGGe3VyhIyQiYJ8eXx/gH95dHBwdHl+goWHiIiIh4WCf3x5d3Z2d3l7fX+BgoKCgYB+fHp5eHh5ent9f4CBgoKBgH58e3p5eXp7fH5/gIGBgYB/fn18e3t7fH1+f4CAgICAf359fHx8fH1+fn+AgICAgH9+fn19fX1+fn9/gICAgH9/fn5+fn5+f39/gICAf39/fn5+fn5/f3+Af4B/f39/fn5+f39/f3+Af39/f39+fn9/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/");
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch {}
  }, [soundEnabled]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await capitalFlowAPI.getData(finnhubKey);
      setData(result);
      if (setCachedData) setCachedData(result);

      // Check for NEW alerts and notify
      const items = result?.unusual_volume || [];
      const newAlerts = [];
      const currentAlertTickers = new Set();

      items.forEach(item => {
        const { level, reasons } = classifyAlert(item);
        if (level >= 2) {
          currentAlertTickers.add(item.ticker);
          if (!prevAlertsRef.current.has(item.ticker)) {
            newAlerts.push({ ...item, alert: { level, reasons } });
          }
        }
      });

      // Fire toast + sound for new critical/important alerts
      if (newAlerts.length > 0) {
        playAlertSound();
        newAlerts.slice(0, 3).forEach(a => {
          const emoji = a.alert.level === 3 ? "🚨" : "⚠️";
          const direction = a.daily_change >= 0 ? `+${a.daily_change}%` : `${a.daily_change}%`;
          toast.warning(
            `${emoji} ${a.ticker} — ${a.vol_ratio}x volume ${direction}\n${a.alert.reasons[0]}`,
            { duration: 8000 }
          );
        });
      }

      prevAlertsRef.current = currentAlertTickers;
      
      // Report alert count to parent for badge
      const totalAlerts = items.filter(i => classifyAlert(i).level >= 2).length;
      onAlertCount?.(totalAlerts);
    } catch (e) {
      console.error("Capital flow error:", e);
    }
    setLoading(false);
  }, [finnhubKey, playAlertSound, onAlertCount]);

  const fetchAIReport = async () => {
    setAiLoading(true);
    try {
      const result = await capitalFlowAPI.getAIReport();
      setAiReport(result.report || "");
    } catch (e) {
      console.error("AI report error:", e);
    }
    setAiLoading(false);
  };

  // Only fetch on first visit (no cached data)
  useEffect(() => { if (!data) fetchData(); }, []);

  // No auto-refresh — user clicks refresh manually

  const unusual = data?.unusual_volume || [];
  const options = data?.options_flow || [];
  const maxVol = unusual.length > 0 ? Math.max(...unusual.map(u => u.vol_ratio)) : 1;

  // Enrich with alert classification
  const enriched = unusual.map(u => ({ ...u, alert: classifyAlert(u) }));
  const alerts = enriched.filter(u => u.alert.level >= 2).sort((a, b) => b.alert.level - a.alert.level);

  // Apply filter
  const filtered = filter === "all" ? enriched
    : filter === "alerts" ? enriched.filter(u => u.alert.level >= 2)
    : filter === "buy" ? enriched.filter(u => u.flow_signal === "ACCUMULATION")
    : enriched.filter(u => u.flow_signal === "DISTRIBUTION");

  const accum = unusual.filter(u => u.flow_signal === "ACCUMULATION").length;
  const distrib = unusual.filter(u => u.flow_signal === "DISTRIBUTION").length;
  const sweeps = unusual.filter(u => u.flow_signal === "EXTREME_VOLUME").length;

  const renderInline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, j) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={j} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
      }
      return <span key={j}>{part}</span>;
    });
  };

  return (
    <div className="glass-card rounded-xl overflow-hidden border border-cyan-500/20" data-testid="capital-flow-panel">
      {/* Header */}
      <div className="px-3 py-2.5 bg-gradient-to-r from-cyan-900/30 to-slate-900/80 border-b border-cyan-500/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-bold text-white">Capital Flow</span>
            <span className="text-[9px] text-cyan-400 bg-cyan-500/15 px-1.5 py-0.5 rounded font-mono">LIVE</span>
            {alerts.length > 0 && (
              <span className="text-[9px] font-bold text-red-400 bg-red-500/20 border border-red-500/30 px-1.5 py-0.5 rounded-full animate-pulse" data-testid="cf-alert-count">
                {alerts.length} alerte{alerts.length > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-[10px]">
              <span className="text-emerald-400 font-mono">{accum} achats</span>
              <span className="text-slate-500">|</span>
              <span className="text-red-400 font-mono">{distrib} ventes</span>
              <span className="text-slate-500">|</span>
              <span className="text-amber-400 font-mono">{sweeps} sweeps</span>
            </div>
            <button onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-1 rounded transition-colors ${soundEnabled ? "text-cyan-400 hover:text-cyan-300" : "text-slate-600 hover:text-slate-400"}`}
              title={soundEnabled ? "Son active" : "Son desactive"} data-testid="cf-sound-toggle">
              <Volume2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={fetchData} className="text-slate-400 hover:text-white transition-colors" data-testid="capital-flow-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
        {/* Tabs + Filters */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex gap-1">
            {[
              { id: "flow", label: "Flux", icon: Zap },
              { id: "options", label: "Options", icon: BarChart3 },
              { id: "report", label: "AI Report", icon: Brain },
            ].map(t => (
              <button key={t.id} onClick={() => { setTab(t.id); if (t.id === "report" && !aiReport) fetchAIReport(); }}
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-semibold transition-all ${
                  tab === t.id ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30" : "text-slate-400 hover:text-slate-200"
                }`} data-testid={`cf-tab-${t.id}`}>
                <t.icon className="w-3 h-3" />
                {t.label}
              </button>
            ))}
          </div>
          {tab === "flow" && (
            <div className="flex gap-1">
              {[
                { id: "all", label: "Tous" },
                { id: "alerts", label: `Alertes (${alerts.length})` },
                { id: "buy", label: "Achats" },
                { id: "sell", label: "Ventes" },
              ].map(f => (
                <button key={f.id} onClick={() => setFilter(f.id)}
                  className={`px-2 py-0.5 rounded text-[9px] font-semibold transition-all ${
                    filter === f.id ? "bg-slate-600/50 text-white" : "text-slate-500 hover:text-slate-300"
                  }`} data-testid={`cf-filter-${f.id}`}>
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Alert Banner */}
      {tab === "flow" && <AlertBanner alerts={alerts.slice(0, 5)} onTickerClick={onTickerClick} />}

      {/* Content */}
      <div className="max-h-[500px] overflow-y-auto scrollbar-thin">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin" />
            <span className="ml-2 text-slate-400 text-sm">Analyse des flux...</span>
          </div>
        ) : tab === "flow" ? (
          <div className="divide-y divide-slate-700/30">
            <div className="grid grid-cols-12 gap-1 px-3 py-1.5 text-[9px] text-slate-500 uppercase font-semibold bg-slate-800/40 sticky top-0 z-10">
              <div className="col-span-3">Ticker</div>
              <div className="col-span-2">Signal</div>
              <div className="col-span-2 text-right">Volume</div>
              <div className="col-span-2 text-right">Variation</div>
              <div className="col-span-1 text-right">RSI</div>
              <div className="col-span-2 text-right">Trend</div>
            </div>
            {filtered.map((u, i) => {
              const alertStyle = ALERT_LEVELS[u.alert.level];
              const isHot = u.alert.level >= 2;
              return (
                <div key={i}
                  className={`grid grid-cols-12 gap-1 px-3 py-1.5 transition-colors cursor-pointer items-center border-l-2
                    ${isHot ? `${alertStyle.bg} ${alertStyle.border} hover:brightness-110` : "border-transparent hover:bg-slate-800/40"}
                    ${u.alert.level === 3 ? "animate-pulse-subtle" : ""}`}
                  onClick={() => onTickerClick?.(u.ticker)} data-testid={`cf-row-${u.ticker}`}>
                  <div className="col-span-3">
                    <div className="flex items-center gap-1.5">
                      {isHot && <AlertTriangle className={`w-3 h-3 shrink-0 ${alertStyle.icon}`} />}
                      <div>
                        <div className="text-[11px] font-bold text-white">{u.ticker}</div>
                        <div className="text-[8px] text-slate-500 truncate">{u.name}</div>
                        {isHot && <div className={`text-[7px] ${alertStyle.text} mt-0.5`}>{u.alert.reasons[0]}</div>}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <FlowBadge signal={u.flow_signal} />
                  </div>
                  <div className="col-span-2 text-right">
                    <VolumeBar ratio={u.vol_ratio} maxRatio={maxVol} />
                  </div>
                  <div className={`col-span-2 text-right text-[11px] font-mono font-semibold ${u.daily_change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    <span className="flex items-center justify-end gap-0.5">
                      {u.daily_change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {u.daily_change >= 0 ? "+" : ""}{u.daily_change}%
                    </span>
                  </div>
                  <div className={`col-span-1 text-right text-[10px] font-mono ${u.rsi < 30 ? "text-emerald-400" : u.rsi > 70 ? "text-red-400" : "text-slate-400"}`}>
                    {u.rsi}
                  </div>
                  <div className="col-span-2 text-right">
                    <span className={`text-[9px] px-1 py-0.5 rounded ${
                      u.trend === "UP" ? "bg-emerald-500/15 text-emerald-400" : u.trend === "DOWN" ? "bg-red-500/15 text-red-400" : "bg-slate-500/15 text-slate-400"
                    }`}>{u.trend}</span>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="text-center py-8 text-slate-500 text-sm">Aucun flux correspondant au filtre</div>
            )}
          </div>
        ) : tab === "options" ? (
          <div className="p-3 space-y-2">
            {options.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">Aucune donnee options disponible</div>
            ) : (
              <>
                <div className="grid grid-cols-7 gap-1 text-[9px] text-slate-500 uppercase font-semibold pb-1 border-b border-slate-700/30">
                  <div className="col-span-1">Ticker</div>
                  <div className="col-span-1 text-right">P/C Ratio</div>
                  <div className="col-span-1 text-right">Calls</div>
                  <div className="col-span-1 text-right">Puts</div>
                  <div className="col-span-1 text-center">Signal</div>
                  <div className="col-span-2">Strikes inhabituels</div>
                </div>
                {options.map((o, i) => (
                  <div key={i} className="grid grid-cols-7 gap-1 items-start py-1.5 border-b border-slate-700/20 hover:bg-slate-800/30 cursor-pointer"
                    onClick={() => onTickerClick?.(o.ticker)} data-testid={`cf-opt-${o.ticker}`}>
                    <div className="col-span-1 text-[11px] font-bold text-white">{o.ticker}</div>
                    <div className={`col-span-1 text-right text-[11px] font-mono font-semibold ${o.put_call_ratio < 0.7 ? "text-emerald-400" : o.put_call_ratio > 1.3 ? "text-red-400" : "text-slate-300"}`}>
                      {o.put_call_ratio}
                    </div>
                    <div className="col-span-1 text-right text-[10px] text-emerald-400 font-mono">{o.total_call_vol.toLocaleString()}</div>
                    <div className="col-span-1 text-right text-[10px] text-red-400 font-mono">{o.total_put_vol.toLocaleString()}</div>
                    <div className={`col-span-1 text-center text-[9px] font-bold ${SIGNAL_COLORS[o.signal]}`}>
                      {o.signal === "BULLISH" ? "HAUSSIER" : o.signal === "BEARISH" ? "BAISSIER" : "NEUTRE"}
                    </div>
                    <div className="col-span-2 space-y-0.5">
                      {(o.unusual_strikes || []).slice(0, 2).map((s, j) => (
                        <div key={j} className="flex items-center gap-1 text-[8px]">
                          <span className={`px-1 rounded ${s.type === "CALL" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                            {s.type}
                          </span>
                          <span className="text-slate-400">${s.strike}</span>
                          <span className="text-white font-mono">{s.ratio}x OI</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        ) : (
          <div className="p-3">
            {aiLoading ? (
              <div className="flex items-center justify-center py-12">
                <Brain className="w-5 h-5 text-cyan-400 animate-pulse" />
                <span className="ml-2 text-slate-400 text-sm">Generation du rapport IA...</span>
              </div>
            ) : aiReport ? (
              <div className="space-y-1 text-[10px] leading-relaxed">
                {aiReport.split("\n").map((line, i) => {
                  const trimmed = line.trim();
                  if (!trimmed) return <div key={i} className="h-1" />;
                  if (/^(\d+\.|#{1,3}\s|[A-Z]{3,}[\s:])/.test(trimmed)) {
                    return <div key={i} className="text-[11px] font-bold text-cyan-300 mt-2">{renderInline(trimmed.replace(/^#+\s*/, ""))}</div>;
                  }
                  if (trimmed.startsWith("-") || trimmed.startsWith("*")) {
                    return <div key={i} className="text-slate-300 pl-2 flex gap-1"><span className="text-cyan-500/60 shrink-0">-</span><span>{renderInline(trimmed.replace(/^[-*]\s*/, ""))}</span></div>;
                  }
                  return <div key={i} className="text-slate-300">{renderInline(trimmed)}</div>;
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Brain className="w-8 h-8 text-cyan-400/30 mx-auto mb-2" />
                <button onClick={fetchAIReport} className="text-cyan-400 text-sm hover:text-cyan-300 transition-colors" data-testid="cf-generate-report">
                  Generer le rapport IA
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      {data && (
        <div className="px-3 py-1.5 border-t border-slate-700/30 flex items-center justify-between text-[9px] text-slate-500">
          <span>{data.total_unusual} flux | {alerts.length} alertes | {data.total_options_tracked} options</span>
          <span>MaJ {new Date(data.updated_at).toLocaleTimeString("fr-FR")} | Auto-refresh 5min</span>
        </div>
      )}
    </div>
  );
}
